/**
 * Модальное окно автоматического сбора факта БДР из внутренних модулей
 * программного комплекса (ОСНОВА.Сметчик КС-2/КС-3, ОСНОВА.Инженер ПТО, ОСНОВА.Поле)
 * без ручной загрузки файлов первички.
 */
import { useState } from 'react';
import styled from 'styled-components';
import {
  useAutoCollectEconRunMutation,
  usePreviewEconAutoCollectQuery,
} from '@shared/api';
import { apiErrorMessage, fmtRub } from '@entities/economic/types';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(10, 12, 16, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 560px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: ${({ theme }) => theme.shadow.lg};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

const StatBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const StatNum = styled.span`
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const StatSub = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

const ContractsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SourcesGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
`;

const ErrorNote = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

export function AutoCollectFactModal({
  period,
  objectScope,
  onClose,
  onCreated,
}: {
  period: string;
  objectScope?: string;
  onClose: () => void;
  onCreated: (runId: string) => void;
}) {
  const { activeProjectName } = useActiveProjectContext();
  const effectiveScope = objectScope?.trim() || activeProjectName || undefined;
  const [err, setErr] = useState<string | null>(null);
  const [includeKs, setIncludeKs] = useState(true);
  const [includeField, setIncludeField] = useState(true);
  const [includeAdjacent, setIncludeAdjacent] = useState(true);

  const previewQ = usePreviewEconAutoCollectQuery({
    period,
    object_scope: effectiveScope,
  });
  const [startCollect, { isLoading: starting }] = useAutoCollectEconRunMutation();

  const p = previewQ.data;
  const contracts = p?.contracts ?? [];

  async function handleStart() {
    setErr(null);
    try {
      const res = await startCollect({
        module: 'bdr',
        period,
        object_scope: effectiveScope,
        include_ks: includeKs,
        include_field: includeField,
        include_adjacent: includeAdjacent,
      }).unwrap();
      onCreated(res.run_id);
    } catch (e) {
      setErr(apiErrorMessage(e) ?? 'Не удалось запустить автоматический сбор факта.');
    }
  }

  return (
    <Backdrop onClick={onClose}>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="auto-collect-title"
        onClick={(e) => e.stopPropagation()}
      >
        <Title id="auto-collect-title">Автосбор факта из смежных модулей</Title>
        <Subtitle>
          Экономист соберёт утверждённые акты КС-2 и справки КС-3 из модуля «Сметчик», а также
          подтверждённые объёмы из «Инженер (ПТО)» и «Поле» за период <b>{period}</b>
          {effectiveScope ? ` по объекту «${effectiveScope}»` : ''}.
        </Subtitle>

        {previewQ.isLoading ? (
          <Subtitle>Проверка доступных источников в БД…</Subtitle>
        ) : previewQ.isError ? (
          <ErrorNote>
            {apiErrorMessage(previewQ.error) ?? 'Не удалось загрузить предпросмотр источников.'}
          </ErrorNote>
        ) : p ? (
          <>
            <StatsGrid>
              <StatBox>
                <StatLabel>Акты КС-2</StatLabel>
                <StatNum>{p.ks2_acts_count ?? 0} шт.</StatNum>
                <StatSub>{fmtRub(p.ks2_total_amount ?? 0)}</StatSub>
              </StatBox>
              <StatBox>
                <StatLabel>Справки КС-3</StatLabel>
                <StatNum>{p.ks3_summaries_count ?? 0} шт.</StatNum>
                <StatSub>{fmtRub(p.ks3_total_amount ?? 0)}</StatSub>
              </StatBox>
              <StatBox>
                <StatLabel>Пакеты ПТО / Поле</StatLabel>
                <StatNum>{p.packages_count ?? 0} шт.</StatNum>
                <StatSub>
                  Договоров: {contracts.length} · Поле: {p.field_facts_count ?? 0}
                </StatSub>
              </StatBox>
            </StatsGrid>

            <SourcesGroup>
              <StatLabel>Включить контуры источников:</StatLabel>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={includeKs}
                  onChange={(e) => setIncludeKs(e.target.checked)}
                />
                <span>Сметчик и ПТО (подписанные КС-2, КС-3, выпущенные комплекты и НВ)</span>
              </CheckboxRow>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={includeField}
                  onChange={(e) => setIncludeField(e.target.checked)}
                />
                <span>Поле и Стройконтроль (подтверждённое выполнение на площадке)</span>
              </CheckboxRow>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={includeAdjacent}
                  onChange={(e) => setIncludeAdjacent(e.target.checked)}
                />
                <span>Смежные прогоны периода (Добыча ПИ, Столовая)</span>
              </CheckboxRow>
            </SourcesGroup>

            {contracts.length > 0 && (
              <div>
                <StatLabel style={{ display: 'block', marginBottom: 6 }}>
                  Обнаруженные договоры за период:
                </StatLabel>
                <ContractsList>
                  {contracts.map((c) => (
                    <Badge key={c} $tone="neutral">
                      {c}
                    </Badge>
                  ))}
                </ContractsList>
              </div>
            )}
          </>
        ) : null}

        {err && <ErrorNote>{err}</ErrorNote>}

        <Actions>
          <Button type="button" $variant="ghost" onClick={onClose} disabled={starting}>
            Отмена
          </Button>
          <Button
            type="button"
            $variant="primary"
            onClick={handleStart}
            disabled={starting || previewQ.isLoading || (!includeKs && !includeField && !includeAdjacent)}
          >
            {starting ? 'Запуск…' : 'Собрать и рассчитать БДР'}
          </Button>
        </Actions>
      </Dialog>
    </Backdrop>
  );
}
